import React, { useEffect, useMemo, useState } from "react";
import useOrganization from "../../organization/hooks/useOrganization";
import { getReportList, getSurveyScores } from "../api/individualResults.api";
import getAverage from "../../../utils/getAverageScore";
import StressCircle from "./StressCircle";
import { formatReportHtml } from "../../../utils/formatReportHtml";
import StressDrawer from "./StressDrawer";
import { useLocation } from "react-router-dom";
import {
  getTeamCompositeScores,
  getUserSurveyScores,
} from "../../TeamcompositeResults/api/team.api";
import { useTeam } from "../../TeamcompositeResults/contexts/TeamContext";
import { useProfile } from "../../auth/hooks/useProfile";
import { useCoach } from "../../coach/contexts/CoachProvider";

const stressKeyMap = [
  { key: "Arguing", label: "Arguing", color: "#f4a261" },
  { key: "Freezing", label: "Freezing", color: "#6c757d" },
  { key: "Retreating", label: "Retreating", color: "#6c757d" },
  { key: "Audacious", label: "Audacious", color: "#ef4444" },
  { key: "Appeasing", label: "Appeasing", color: "#6c757d" },
];

interface Props {
  isPrint?: boolean;
}

const StressInsightsSection: React.FC<Props> = ({ isPrint = false }) => {
  const { currentOrganization } = useOrganization();
  const { isCoach } = useProfile();
  const { defaultSurveyId: coachSurveyId } = useCoach();

  const [stressData, setStressData] = useState<any[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const selectedUser = location.state?.user;
  const clickedTeamId = location.state?.teamId;
  const { selectedTeam } = useTeam();

  const questionnaireId = isCoach
    ? coachSurveyId
    : currentOrganization?.default_survey;

  /**
   * Fetch survey scores
   */
  useEffect(() => {
    if (!questionnaireId || questionnaireId <= 0) return;

    const loadScores = async () => {
      try {
        let res;

        if (selectedUser?.id) {
            res = await getUserSurveyScores({
              questionnaire_id: Number(questionnaireId),
              user_id: Number(selectedUser.id),
            });

          } else if (clickedTeamId) {
            res = await getTeamCompositeScores({
              questionnaire_id: Number(questionnaireId),
              team_id: Number(clickedTeamId),
            });

          } else {
            res = await getSurveyScores(Number(questionnaireId));
          }
        const scores = res.data?.scores || res.data || {};

        const mapped = stressKeyMap.map((item) => ({
          label: item.label,
          value: +getAverage(scores[item.key]).toFixed(0),
          color: item.color,
        }));

        setStressData(mapped);
      } catch (err) {
        console.error("Score loading error", err);
      }
    };

    loadScores();
  }, [questionnaireId, selectedUser, selectedTeam, clickedTeamId]);

  /**
   * Sort stress values
   */
  const enhancedStressData = useMemo(() => {
    const sorted = [...stressData].sort((a, b) => b.value - a.value);

    return sorted.map((item, index) => ({
      ...item,
      type: index === 0 ? "Primary" : index === 1 ? "Secondary" : undefined,
    }));
  }, [stressData]);

  /**
   * Fetch insights
   */
  useEffect(() => {
    if (!enhancedStressData.length) return;

    const fetchInsights = async () => {
      try {
        const res = await getReportList({
          page: 1,
          limit: 10,
          search: "Stress",
        });

        const report = res.data.rows.find(
          (item: any) => item.name === "Unprofessional Stress Response",
        );

        if (!report) return;

        const parsed = JSON.parse(report.values);

        const reportsToShow = isPrint
          ? enhancedStressData
          : enhancedStressData.slice(0, 2);

        const reportsData = reportsToShow
          .map(
            (item) =>
              parsed.find(
                (r: any) =>
                  r.attributes.title.toLowerCase() === item.label.toLowerCase(),
              )?.attributes,
          )
          .filter(Boolean);

        setReports(reportsData);
      } catch (err) {
        console.error("Error loading stress insights:", err);
      }
    };

    fetchInsights();
  }, [enhancedStressData, isPrint]);

  /**
   * Drawer open
   */
  const openDrawer = async (label: string) => {
    if (isPrint) return;

    setDrawerOpen(true);
    setLoading(true);

    try {
      const res = await getReportList({
        page: 1,
        limit: 10,
        search: "Stress",
      });

      const report = res.data.rows.find(
        (item: any) => item.name === "Unprofessional Stress Response",
      );

      if (!report) return;

      const parsed = JSON.parse(report.values);

      const selected = parsed.find(
        (item: any) =>
          item.attributes.title.toLowerCase() === label.toLowerCase(),
      );

      setReportData(selected?.attributes || null);
    } catch (err) {
      console.error("Drawer report error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white py-5 print:break-before-page">
      <div className="max-w-[1155px] mx-auto px-5">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-[140px] items-start">
          {/* LEFT SIDE */}
          <div className="w-full lg:w-[38%] self-start lg:sticky lg:top-6 print:static">
            <h2 className="text-2xl sm:text-3xl lg:text-[38px] text-[#26295e] mb-8 lg:mb-10">
              Unprofessional Stress Response
            </h2>

            <div className="text-center">
              <div className="flex justify-center gap-[60px] flex-wrap">
                {stressData.map((item) => (
                  <StressCircle
                    key={item.label}
                    label={item.label}
                    value={item.value}
                    color={item.color}
                    onClick={() => openDrawer(item.label)}
                    isPrint={isPrint}
                    interactive={true}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE */}
          <div className="w-full lg:w-[62%]">
            {reports.map((report, index) => (
              <div
                key={index}
                className={`${index !== 0 ? "mt-10 print:break-before-page" : ""}`}
              >
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 18,
                    color: "#3b519c",
                    marginRight: 10,
                  }}
                >
                  INSIGHTS
                </span>

                <span style={{ fontWeight: 500, fontSize: 18 }}>
                  {report.title}
                </span>

                <div
                  className="text-[17px] leading-8 text-[#4b507d]"
                  dangerouslySetInnerHTML={{
                    __html: formatReportHtml(report.details),
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {!isPrint && (
        <StressDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          loading={loading}
          report={reportData}
        />
      )}
    </section>
  );
};

export default StressInsightsSection;
